import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Download, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm" data-testid={testid}>
      <div className="label-xs">{label}</div>
      <div className="mt-1 text-xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

function Occ({ pct }) {
  const c = pct >= 70 ? "text-emerald-700" : pct >= 40 ? "text-amber-600" : "text-rose-600";
  return <b className={c}>{pct}%</b>;
}

export default function RoutePerformance() {
  const { user } = useAuth();
  const isSM = user?.role === "station_master";
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(daysAgo(0));
  const [stationId, setStationId] = useState("");
  const [stations, setStations] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isSM) api.get("/admin/stations").then(({ data: d }) => setStations(d)).catch(() => {});
  }, [isSM]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/admin/route-performance", { params: { date_from: from, date_to: to, station_id: stationId || undefined } });
      setData(d);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [from, to, stationId]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const rows = data?.rows || [];
    const head = ["Timetable", "From", "To", "Trips", "Operated", "Cancelled", "Upcoming", "Not run", "Seats sold", "Capacity", "Occupancy %", "Revenue (GHS)"];
    const lines = [head.join(",")].concat(rows.map((r) => [
      `"${(r.route_name || "").replace(/"/g, "'")}"`, r.origin_city, r.destination_city, r.trips_total, r.operated, r.cancelled, r.upcoming, r.not_run, r.seats, r.capacity, r.occupancy_pct, r.revenue,
    ].join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `route-performance_${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const t = data?.totals;
  return (
    <div className="space-y-6" data-testid="route-performance-page">
      <div>
        <h2 className="font-display text-2xl font-bold">Route performance</h2>
        <p className="text-sm text-slate-500">How each timetable performed over the selected dates — trips run, seats sold, occupancy and fare revenue.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-100 bg-slate-50/60 p-4">
        <label className="text-sm font-semibold text-slate-600">From<Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-44 bg-white" data-testid="rp-from" /></label>
        <label className="text-sm font-semibold text-slate-600">To<Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="mt-1 w-44 bg-white" data-testid="rp-to" /></label>
        {!isSM && (
          <label className="text-sm font-semibold text-slate-600">Station
            <select value={stationId} onChange={(e) => setStationId(e.target.value)} className="mt-1 block w-56 rounded-xl border border-slate-200 bg-white p-2.5 text-sm" data-testid="rp-station">
              <option value="">All stations</option>
              {stations.map((s) => <option key={s.id} value={s.id}>{s.city} - {s.name}</option>)}
            </select>
          </label>
        )}
        <Button variant="outline" onClick={load} disabled={loading} data-testid="rp-refresh">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}</Button>
        <Button variant="outline" onClick={exportCsv} disabled={!data?.rows?.length} data-testid="rp-export"><Download className="h-4 w-4" />Export CSV</Button>
      </div>

      {t && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="rp-summary">
          <Stat label="Trips (operated)" value={`${t.trips_total} (${t.operated})`} testid="rp-total-trips" />
          <Stat label="Seats sold / capacity" value={`${t.seats} / ${t.capacity}`} testid="rp-total-seats" />
          <Stat label="Occupancy" value={`${t.occupancy_pct}%`} testid="rp-total-occupancy" />
          <Stat label="Fare revenue" value={money(t.revenue)} testid="rp-total-revenue" />
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-slate-100">
        <table className="w-full text-sm" data-testid="rp-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Timetable</th>
              <th className="px-4 py-3 text-right">Trips</th>
              <th className="px-4 py-3 text-right">Operated</th>
              <th className="px-4 py-3 text-right">Cancelled</th>
              <th className="px-4 py-3 text-right">Upcoming</th>
              <th className="px-4 py-3 text-right">Not run</th>
              <th className="px-4 py-3 text-right">Seats</th>
              <th className="px-4 py-3 text-right">Occupancy</th>
              <th className="px-4 py-3 text-right">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {(!data?.rows?.length) && <tr><td colSpan={9} className="px-4 py-6 text-center text-slate-400" data-testid="rp-empty">No trips for the selected dates.</td></tr>}
            {(data?.rows || []).map((r) => (
              <tr key={r.route_id} className="border-t border-slate-100" data-testid={`rp-row-${r.route_id}`}>
                <td className="px-4 py-2.5">
                  <div className="font-semibold text-slate-800">{r.route_name}{!r.active && <span className="ml-1 chip bg-amber-50 text-amber-700">Paused</span>}</div>
                  <div className="text-xs text-slate-400">{r.origin_city} → {r.destination_city}</div>
                </td>
                <td className="px-4 py-2.5 text-right">{r.trips_total}</td>
                <td className="px-4 py-2.5 text-right">{r.operated}</td>
                <td className="px-4 py-2.5 text-right">{r.cancelled}</td>
                <td className="px-4 py-2.5 text-right">{r.upcoming}</td>
                <td className="px-4 py-2.5 text-right text-slate-500">{r.not_run}</td>
                <td className="px-4 py-2.5 text-right">{r.seats}/{r.capacity}</td>
                <td className="px-4 py-2.5 text-right"><Occ pct={r.occupancy_pct} /></td>
                <td className="px-4 py-2.5 text-right font-semibold">{money(r.revenue)}</td>
              </tr>
            ))}
          </tbody>
          {t && data?.rows?.length > 0 && (
            <tfoot><tr className="border-t-2 border-slate-200 bg-slate-50 font-bold">
              <td className="px-4 py-3">Total</td>
              <td className="px-4 py-3 text-right">{t.trips_total}</td>
              <td className="px-4 py-3 text-right">{t.operated}</td>
              <td className="px-4 py-3 text-right">{t.cancelled}</td>
              <td className="px-4 py-3 text-right">{t.upcoming}</td>
              <td className="px-4 py-3 text-right">{t.not_run}</td>
              <td className="px-4 py-3 text-right">{t.seats}/{t.capacity}</td>
              <td className="px-4 py-3 text-right">{t.occupancy_pct}%</td>
              <td className="px-4 py-3 text-right text-[#0052FF]">{money(t.revenue)}</td>
            </tr></tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
